import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import * as R from 'ramda'
import { getOrderDetail } from '@/service/orderTest'
import {
  getOrderApiByType,
  getMaternityCenterOrderProfile,
  normalizeMaternityOrderDetailFromApiResult,
  getMaternityCenterSchoiceProjects,
  getMaternityCenterChanKangProjects,
  getMaternityCenterOrderProjectArray,
  getMaternityCenterOrderVoucher,
  getMaternityCenterOrderFamily,
  getMaternityCenterOrderPromotion,
  getMaternityCenterOrderGift,
  getMaternityCenterAffects,
  getMaternityCenterSpecialNote
} from '@/service/orderTypeApis'
import { addressToCoord } from '@/utils/geocoding'
import { getProjectPlanUnassigned } from '@/service/dispatchPage'

/**
 * 订单列表接口结果在 Redux 中的存放分支，与「订单列表」页、派单页一一对应，避免两页互跳时 query/结果互相覆盖。
 * @type {{ ORDER_LIST_PAGE: 'orderListPage', DISPATCH_PAGE: 'dispatchOrderList' }}
 */
export const ORDER_LIST_REDUX_SCOPE = {
  ORDER_LIST_PAGE: 'orderListPage',
  DISPATCH_PAGE: 'dispatchOrderList'
}

// ==================== 数据处理方法 ====================

/**
 * 产后复原项目列表：按 typeName 是否包含「赠送」分组，并排序 projectArray
 * @param {Array} list - 接口返回的原始列表
 * @returns {{ data: Array, givingProjectList: Array, projectArray: Array }}
 */
const processChanKangList = (list) => {
  const raw = Array.isArray(list) ? list : (list?.data || [])
  const arr = Array.isArray(raw) ? raw : []
  const [givingProjectList, nonGivingList] = R.partition(
    (r) => (r?.typeName ?? '').includes('赠送'),
    arr
  )
  const projectArray = R.sort((a, b) => {
    const typeA = String(a?.typeName ?? '')
    const typeB = String(b?.typeName ?? '')
    if (typeA !== typeB) return typeA.localeCompare(typeB)
    const useNumA = Number(a?.useNum)
    const useNumB = Number(b?.useNum)
    return (Number.isFinite(useNumB) ? useNumB : 0) - (Number.isFinite(useNumA) ? useNumA : 0)
  }, nonGivingList)
  return { data: arr, givingProjectList, projectArray }
}

/**
 * 项目列表按 type 字段（1、2、3）分类存储
 * @param {Array} list - 接口返回的原始列表
 * @returns {{ type1: Array, type2: Array, type3: Array }}
 */
const processProjectArrayByType = (list) => {
  const raw = Array.isArray(list) ? list : (list?.data || [])
  const arr = Array.isArray(raw) ? raw : []
  const grouped = R.groupBy((r) => String(r?.projectType ?? ''), arr)
  return {
    type1: grouped['1'] || [],
    type2: grouped['2'] || [],
    type3: grouped['3'] || []
  }
}

/**
 * 根据数据格式类型重组数据（用于 fetchOrdersByType）
 * 数据格式类型：default 默认列表 | heatMap 热力图点位
 * @param {Object} data - 接口返回的原始数据
 * @param {string} dataType - 数据格式类型，决定输出结构
 * @param {Object} [query] - 请求参数，用于保留 pageSize、pageNumber
 * @returns {Promise<Object>} 处理后的数据，供 orderListPage / dispatchOrderList 的 data 使用
 */
const processSingleDataType = async (data, dataType, query = {}) => {
  const emptyData = { records: [], totalRow: 0, pageNumber: 1, pageSize: 20 }
  const raw = data || emptyData

  if (!dataType || dataType === 'default') {
    return raw
  }

  switch (dataType) {
    case 'heatMap': {
      const records = raw.records || []
      const validRecords = records.filter((item) => {
        const hasCoord = (item.points?.[0] != null && item.points?.[1] != null) ||
          (item.longitude != null && item.latitude != null)
        const hasAddress = typeof item.customerAddress === 'string' && item.customerAddress.trim().length > 0
        return hasCoord || hasAddress
      })
      const formatData = []
      const useNominatimOnly = false
      const delay = (ms) => new Promise((r) => setTimeout(r, ms))

      for (let i = 0; i < validRecords.length; i++) {
        const item = validRecords[i]
        const name = item.customerName || item.maternalInfo?.name || ''
        let coord = null

        const lng = item.points?.[0] ?? item.longitude
        const lat = item.points?.[1] ?? item.latitude
        if (lng != null && lat != null) {
          coord = [Number(lng), Number(lat)]
        } else {
          const address = item.customerAddress || ''
          if (address.trim()) {
            coord = await addressToCoord(address, { city: '广州', useNominatimOnly })
            if (useNominatimOnly) {
              await delay(1100)
            } else {
              await delay(340)
            }
          }
        }
        if (coord) formatData.push({ name, coord, value: 30 })
      }

      const totalRequested = records.length
      const totalValidAddress = validRecords.length
      const totalWithCoord = formatData.length
      if (totalRequested !== totalValidAddress) {
        console.warn(`[heatMap] 接口返回 ${totalRequested} 条，${totalValidAddress} 条有有效地址（坐标或 customerAddress）`)
      }
      if (totalValidAddress !== totalWithCoord) {
        console.warn(
          `[heatMap] 有效地址 ${totalValidAddress} 条，仅 ${totalWithCoord} 条地理编码成功（${totalValidAddress - totalWithCoord} 条编码失败）`
        )
      }

      return {
        ...raw,
        records: formatData,
        pageSize: query.pageSize ?? raw.pageSize,
        pageNumber: query.pageNumber ?? raw.pageNumber
      }
    }
    default:
      return raw
  }
}

// ==================== 初始状态 ====================

const emptyOrderListBranch = () => ({
  fetching: false,
  error: null,
  query: {},
  data: {
    records: [],
    totalRow: 0,
    pageNumber: 1,
    pageSize: 20
  }
})

const initialState = {
  // 订单管理 → 订单列表页（与派单页分库存储）
  orderListPage: emptyOrderListBranch(),
  // 派单模块列表（筛选、分页等）
  dispatchOrderList: emptyOrderListBranch(),
  // 订单详情状态（为各字段提供初始化结构，避免组件中大量兜底）
  detail: {
    fetching: false,
    error: null,
    data: {
      // 月子中心订单详情结构兜底，字段命名与后端保持一致
      maternalInfo: {},
      orderState: [],
      signInfo: [],
      promotion: [],
      gift: [],
      voucherGift: [],
      familyList: [],
      orderChangeInfo: {},
      sendMoney: {}
    }
  },
  // 月子中心 - 自选项目列表
  maternityCenterSchoiceProjects: {
    fetching: false,
    error: null,
    data: []
  },
  // 月子中心 - 产后复原项目列表（含 groupBy 后的 givingProjectList / projectArray）
  maternityCenterChanKangProjects: {
    fetching: false,
    error: null,
    data: [],
    givingProjectList: [],
    projectArray: []
  },
  // 月子中心 - 项目列表（order-total/{id}/projects），按 type 1/2/3 分类存储
  maternityCenterOrderProjectArray: {
    fetching: false,
    error: null,
    type1: [],
    type2: [],
    type3: []
  },
  // 月子中心 - 代金券
  maternityCenterOrderVoucher: {
    fetching: false,
    error: null,
    data: []
  },
  // 月子中心 - 家属信息
  maternityCenterOrderFamily: {
    fetching: false,
    error: null,
    data: []
  },
  // 月子中心 - 促销信息
  maternityCenterOrderPromotion: {
    fetching: false,
    error: null,
    data: []
  },
  // 月子中心 - 赠品
  maternityCenterOrderGift: {
    fetching: false,
    error: null,
    data: []
  },
  // 月子中心 - 预订单操作记录
  maternityCenterAffects: {
    fetching: false,
    error: null,
    data: []
  },
  // 月子中心 - 特别声明内容
  maternityCenterSpecialNote: {
    fetching: false,
    error: null,
    data: null
  }
}

// ==================== 异步Action ====================

/**
 * 根据订单类型异步获取订单列表的thunk action
 * @param {Object} params - 参数对象
 * @param {Object} params.query - 查询参数对象
 * @param {string} params.orderType - 订单类型（决定调用哪个 API）
 * @param {string} [params.dataType='default'] - 数据格式类型（决定 processSingleDataType 如何重组数据：default | heatMap）
 * @returns {Promise} 返回处理后的API响应数据
 */
/**
 * @param {Object} params.query - 传给接口的查询参数（API 格式）
 * @param {Object} [params.queryForStore] - 可选，存入 Redux 的 query（如 UI 格式）；不传则存 query
 * @param {'orderList'|'dispatchUnassigned'} [params.maternityCenterListMode='orderList'] -
 *   月子中心列表：派单页「新订单」Tab 与订单列表「待入住」筛选均可能为 checkInStatus=1，仅派单需走未分配项目计划接口
 * @param {string} [params.orderListScope=orderListPage] - 见 ORDER_LIST_REDUX_SCOPE，结果写入对应分支
 * @param {boolean} [params.silent=false] - 为 true 时不置列表 fetching、不触发左侧 Spin，失败不清空 data（供定时后台刷新）
 */
export const fetchOrdersByType = createAsyncThunk(
  'order/fetchOrdersByType',
  async (
    {
      query = {},
      queryForStore,
      orderType,
      dataType = 'default',
      maternityCenterListMode = 'orderList',
      orderListScope = ORDER_LIST_REDUX_SCOPE.ORDER_LIST_PAGE,
      silent = false
    },
    { rejectWithValue }
  ) => {
    try {
      if (
        orderType === 'maternity_center' &&
        maternityCenterListMode === 'dispatchUnassigned' &&
        String(query.checkInStatus) === '1'
      ) {
        const { checkInStatus: _c, pageNumber: _p, ...rest } = query
        const apiParams = {}
        if (rest.storeId != null && String(rest.storeId).trim() !== '') {
          apiParams.storeId = rest.storeId
        }
        if (rest.keyword != null && String(rest.keyword).trim() !== '') {
          apiParams.keyword = rest.keyword
        }
        const response = await getProjectPlanUnassigned(apiParams)
        const rawList = response?.data
        let list = Array.isArray(rawList) ? rawList : []
        const kw = (rest.keyword || '').trim().toLowerCase()
        if (kw) {
          list = list.filter((r) =>
            [r.memberName, r.roomName, r.projectName, r.technicianName].some((f) =>
              String(f || '')
                .toLowerCase()
                .includes(kw)
            )
          )
        }
        const data = {
          records: list.map((r) => ({ ...r, _rowKind: 'unassignedProjectPlan' })),
          totalRow: list.length,
          pageNumber: 1,
          pageSize: query.pageSize ?? 20
        }
        return { data, query, queryForStore, orderType }
      }
      const apiFunction = getOrderApiByType(orderType)
      const response = await apiFunction(query)
      const data = await processSingleDataType(response.data, dataType, query)

      return {
        data,
        query,
        queryForStore,
        orderType
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || `获取${orderType}订单列表失败`)
    }
  }
)

/**
 * 异步获取订单详情的thunk action
 * @param {string|number} orderId - 订单ID
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchOrderDetail = createAsyncThunk(
  'order/fetchOrderDetail',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await getOrderDetail(orderId)
      return {
        data: response.data
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取订单详情失败')
    }
  }
)

/**
 * 异步获取月子中心订单详情的thunk action
 * @param {Object} params - 参数对象
 * @param {string|number} params.id - 订单ID
 * @param {Object} params.query - 查询参数对象（可选）
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchMaternityCenterOrderProfile = createAsyncThunk(
  'order/fetchMaternityCenterOrderProfile',
  async ({ id, query = {} }, { rejectWithValue }) => {
    try {
      const response = await getMaternityCenterOrderProfile(id, query)
      return {
        data: normalizeMaternityOrderDetailFromApiResult(response),
        id
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取月子中心订单详情失败')
    }
  }
)

/**
 * 异步获取月子中心自选项目列表
 * @param {Object} params - 参数对象
 * @param {string|number} params.id - 订单ID
 * @param {Object} params.query - 查询参数对象（可选）
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchMaternityCenterSchoiceProjects = createAsyncThunk(
  'order/fetchMaternityCenterSchoiceProjects',
  async ({ id, query = {} }, { rejectWithValue }) => {
    try {
      const response = await getMaternityCenterSchoiceProjects(id, query)
      return {
        data: response.data,
        id
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取月子中心自选项目列表失败')
    }
  }
)

/**
 * 异步获取月子中心产后复原项目列表
 * @param {Object} params - 参数对象
 * @param {string|number} params.id - 订单ID
 * @param {Object} params.query - 查询参数对象（可选）
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchMaternityCenterChanKangProjects = createAsyncThunk(
  'order/fetchMaternityCenterChanKangProjects',
  async ({ id, query = {} }, { rejectWithValue }) => {
    try {
      const response = await getMaternityCenterChanKangProjects(id, query)
      return {
        data: response.data,
        id
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取月子中心产后复原项目列表失败')
    }
  }
)

/**
 * 异步获取月子中心项目列表（order-total/{id}/projects）
 * @param {Object} params - 参数对象
 * @param {string|number} params.id - 订单ID
 * @param {Object} params.query - 查询参数对象（可选）
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchMaternityCenterOrderProjectArray = createAsyncThunk(
  'order/fetchMaternityCenterOrderProjectArray',
  async ({ id, query = {} }, { rejectWithValue }) => {
    try {
      const response = await getMaternityCenterOrderProjectArray(id, query)
      return {
        data: response.data,
        id
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取月子中心项目列表失败')
    }
  }
)

/**
 * 异步获取月子中心代金券
 * @param {Object} params - 参数对象
 * @param {string|number} params.id - 订单ID
 * @param {Object} params.query - 查询参数对象（可选）
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchMaternityCenterOrderVoucher = createAsyncThunk(
  'order/fetchMaternityCenterOrderVoucher',
  async ({ id, query = {} }, { rejectWithValue }) => {
    try {
      const response = await getMaternityCenterOrderVoucher(id, query)
      return {
        data: response.data,
        id
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取月子中心代金券失败')
    }
  }
)

/**
 * 异步获取月子中心家属信息
 * @param {Object} params - 参数对象
 * @param {string|number} params.id - 订单ID
 * @param {Object} params.query - 查询参数对象（可选）
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchMaternityCenterOrderFamily = createAsyncThunk(
  'order/fetchMaternityCenterOrderFamily',
  async ({ id, query = {} }, { rejectWithValue }) => {
    try {
      const response = await getMaternityCenterOrderFamily(id, query)
      return {
        data: response.data,
        id
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取月子中心家属信息失败')
    }
  }
)

/**
 * 异步获取月子中心促销信息
 * @param {Object} params - 参数对象
 * @param {string|number} params.id - 订单ID
 * @param {Object} params.query - 查询参数对象（可选）
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchMaternityCenterOrderPromotion = createAsyncThunk(
  'order/fetchMaternityCenterOrderPromotion',
  async ({ id, query = {} }, { rejectWithValue }) => {
    try {
      const response = await getMaternityCenterOrderPromotion(id, query)
      return {
        data: response.data,
        id
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取月子中心促销信息失败')
    }
  }
)

/**
 * 异步获取月子中心赠品
 * @param {Object} params - 参数对象
 * @param {string|number} params.id - 订单ID
 * @param {Object} params.query - 查询参数对象（可选）
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchMaternityCenterOrderGift = createAsyncThunk(
  'order/fetchMaternityCenterOrderGift',
  async ({ id, query = {} }, { rejectWithValue }) => {
    try {
      const response = await getMaternityCenterOrderGift(id, query)
      return {
        data: response.data,
        id
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取月子中心赠品失败')
    }
  }
)

/**
 * 异步获取月子中心预订单操作记录
 * @param {Object} params - 参数对象
 * @param {string|number} params.id - 订单ID
 * @param {Object} params.query - 查询参数对象（可选）
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchMaternityCenterAffects = createAsyncThunk(
  'order/fetchMaternityCenterAffects',
  async ({ id, query = {} }, { rejectWithValue }) => {
    try {
      const response = await getMaternityCenterAffects(id, query)
      return {
        data: response.data,
        id
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取月子中心预订单操作记录失败')
    }
  }
)

/**
 * 异步获取月子中心特别声明内容
 * @param {Object} params - 参数对象
 * @param {Object} params.query - 查询参数对象（可选）
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchMaternityCenterSpecialNote = createAsyncThunk(
  'order/fetchMaternityCenterSpecialNote',
  async ({ query = {} } = {}, { rejectWithValue }) => {
    try {
      const response = await getMaternityCenterSpecialNote(query)
      return {
        data: response.data
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取月子中心特别声明内容失败')
    }
  }
)

// ==================== Redux Slice ====================

const maternityCenterOrderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    /**
     * 重置订单列表状态
     */
    resetOrderListState: (state) => {
      state.orderListPage = emptyOrderListBranch()
      state.dispatchOrderList = emptyOrderListBranch()
    },
    /** 仅清空派单模块列表（dispatchOrderList），不影响订单列表页 orderListPage */
    resetDispatchOrderListState: (state) => {
      state.dispatchOrderList = emptyOrderListBranch()
    },
    /**
     * 重置订单详情状态
     */
    resetOrderDetailState: (state) => {
      state.detail = {
        fetching: false,
        error: null,
        data: null
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // ========== 获取订单详情 ==========
      .addCase(fetchOrderDetail.pending, (state) => {
        state.detail.fetching = true
        state.detail.error = null
      })
      .addCase(fetchOrderDetail.fulfilled, (state, action) => {
        state.detail.fetching = false
        state.detail.error = null
        state.detail.data = action.payload.data
      })
      .addCase(fetchOrderDetail.rejected, (state, action) => {
        state.detail.fetching = false
        state.detail.error = action.payload
        state.detail.data = null
      })
      // ========== 获取月子中心订单详情 ==========
      .addCase(fetchMaternityCenterOrderProfile.pending, (state) => {
        state.detail.fetching = true
        state.detail.error = null
      })
      .addCase(fetchMaternityCenterOrderProfile.fulfilled, (state, action) => {
        state.detail.fetching = false
        state.detail.error = null
        state.detail.data = action.payload.data
      })
      .addCase(fetchMaternityCenterOrderProfile.rejected, (state, action) => {
        state.detail.fetching = false
        state.detail.error = action.payload
        state.detail.data = null
      })
      // ========== 获取月子中心自选项目列表 ==========
      .addCase(fetchMaternityCenterSchoiceProjects.pending, (state) => {
        state.maternityCenterSchoiceProjects.fetching = true
        state.maternityCenterSchoiceProjects.error = null
      })
      .addCase(fetchMaternityCenterSchoiceProjects.fulfilled, (state, action) => {
        state.maternityCenterSchoiceProjects.fetching = false
        state.maternityCenterSchoiceProjects.error = null
        state.maternityCenterSchoiceProjects.data = action.payload.data || []
      })
      .addCase(fetchMaternityCenterSchoiceProjects.rejected, (state, action) => {
        state.maternityCenterSchoiceProjects.fetching = false
        state.maternityCenterSchoiceProjects.error = action.payload
        state.maternityCenterSchoiceProjects.data = []
      })
      // ========== 获取月子中心产后复原项目列表 ==========
      .addCase(fetchMaternityCenterChanKangProjects.pending, (state) => {
        state.maternityCenterChanKangProjects.fetching = true
        state.maternityCenterChanKangProjects.error = null
      })
      .addCase(fetchMaternityCenterChanKangProjects.fulfilled, (state, action) => {
        state.maternityCenterChanKangProjects.fetching = false
        state.maternityCenterChanKangProjects.error = null
        const { data, givingProjectList, projectArray } = processChanKangList(action.payload.data)
        state.maternityCenterChanKangProjects.data = data
        state.maternityCenterChanKangProjects.givingProjectList = givingProjectList
        state.maternityCenterChanKangProjects.projectArray = projectArray
      })
      .addCase(fetchMaternityCenterChanKangProjects.rejected, (state, action) => {
        state.maternityCenterChanKangProjects.fetching = false
        state.maternityCenterChanKangProjects.error = action.payload
        state.maternityCenterChanKangProjects.data = []
        state.maternityCenterChanKangProjects.givingProjectList = []
        state.maternityCenterChanKangProjects.projectArray = []
      })
      // ========== 获取月子中心项目列表（projects） ==========
      .addCase(fetchMaternityCenterOrderProjectArray.pending, (state) => {
        state.maternityCenterOrderProjectArray.fetching = true
        state.maternityCenterOrderProjectArray.error = null
      })
      .addCase(fetchMaternityCenterOrderProjectArray.fulfilled, (state, action) => {
        state.maternityCenterOrderProjectArray.fetching = false
        state.maternityCenterOrderProjectArray.error = null
        const { type1, type2, type3 } = processProjectArrayByType(action.payload.data)
        state.maternityCenterOrderProjectArray.type1 = type1
        state.maternityCenterOrderProjectArray.type2 = type2
        state.maternityCenterOrderProjectArray.type3 = type3
      })
      .addCase(fetchMaternityCenterOrderProjectArray.rejected, (state, action) => {
        state.maternityCenterOrderProjectArray.fetching = false
        state.maternityCenterOrderProjectArray.error = action.payload
        state.maternityCenterOrderProjectArray.type1 = []
        state.maternityCenterOrderProjectArray.type2 = []
        state.maternityCenterOrderProjectArray.type3 = []
      })
      // ========== 获取月子中心代金券 ==========
      .addCase(fetchMaternityCenterOrderVoucher.pending, (state) => {
        state.maternityCenterOrderVoucher.fetching = true
        state.maternityCenterOrderVoucher.error = null
      })
      .addCase(fetchMaternityCenterOrderVoucher.fulfilled, (state, action) => {
        state.maternityCenterOrderVoucher.fetching = false
        state.maternityCenterOrderVoucher.error = null
        state.maternityCenterOrderVoucher.data = action.payload.data || []
      })
      .addCase(fetchMaternityCenterOrderVoucher.rejected, (state, action) => {
        state.maternityCenterOrderVoucher.fetching = false
        state.maternityCenterOrderVoucher.error = action.payload
        state.maternityCenterOrderVoucher.data = []
      })
      // ========== 获取月子中心家属信息 ==========
      .addCase(fetchMaternityCenterOrderFamily.pending, (state) => {
        state.maternityCenterOrderFamily.fetching = true
        state.maternityCenterOrderFamily.error = null
      })
      .addCase(fetchMaternityCenterOrderFamily.fulfilled, (state, action) => {
        state.maternityCenterOrderFamily.fetching = false
        state.maternityCenterOrderFamily.error = null
        state.maternityCenterOrderFamily.data = action.payload.data || []
      })
      .addCase(fetchMaternityCenterOrderFamily.rejected, (state, action) => {
        state.maternityCenterOrderFamily.fetching = false
        state.maternityCenterOrderFamily.error = action.payload
        state.maternityCenterOrderFamily.data = []
      })
      // ========== 获取月子中心促销信息 ==========
      .addCase(fetchMaternityCenterOrderPromotion.pending, (state) => {
        state.maternityCenterOrderPromotion.fetching = true
        state.maternityCenterOrderPromotion.error = null
      })
      .addCase(fetchMaternityCenterOrderPromotion.fulfilled, (state, action) => {
        state.maternityCenterOrderPromotion.fetching = false
        state.maternityCenterOrderPromotion.error = null
        state.maternityCenterOrderPromotion.data = action.payload.data || []
      })
      .addCase(fetchMaternityCenterOrderPromotion.rejected, (state, action) => {
        state.maternityCenterOrderPromotion.fetching = false
        state.maternityCenterOrderPromotion.error = action.payload
        state.maternityCenterOrderPromotion.data = []
      })
      // ========== 获取月子中心赠品 ==========
      .addCase(fetchMaternityCenterOrderGift.pending, (state) => {
        state.maternityCenterOrderGift.fetching = true
        state.maternityCenterOrderGift.error = null
      })
      .addCase(fetchMaternityCenterOrderGift.fulfilled, (state, action) => {
        state.maternityCenterOrderGift.fetching = false
        state.maternityCenterOrderGift.error = null
        state.maternityCenterOrderGift.data = action.payload.data || []
      })
      .addCase(fetchMaternityCenterOrderGift.rejected, (state, action) => {
        state.maternityCenterOrderGift.fetching = false
        state.maternityCenterOrderGift.error = action.payload
        state.maternityCenterOrderGift.data = []
      })
      // ========== 获取月子中心预订单操作记录 ==========
      .addCase(fetchMaternityCenterAffects.pending, (state) => {
        state.maternityCenterAffects.fetching = true
        state.maternityCenterAffects.error = null
      })
      .addCase(fetchMaternityCenterAffects.fulfilled, (state, action) => {
        state.maternityCenterAffects.fetching = false
        state.maternityCenterAffects.error = null
        state.maternityCenterAffects.data = action.payload.data || []
      })
      .addCase(fetchMaternityCenterAffects.rejected, (state, action) => {
        state.maternityCenterAffects.fetching = false
        state.maternityCenterAffects.error = action.payload
        state.maternityCenterAffects.data = []
      })
      // ========== 获取月子中心特别声明内容 ==========
      .addCase(fetchMaternityCenterSpecialNote.pending, (state) => {
        state.maternityCenterSpecialNote.fetching = true
        state.maternityCenterSpecialNote.error = null
      })
      .addCase(fetchMaternityCenterSpecialNote.fulfilled, (state, action) => {
        state.maternityCenterSpecialNote.fetching = false
        state.maternityCenterSpecialNote.error = null
        state.maternityCenterSpecialNote.data = action.payload.data ?? null
      })
      .addCase(fetchMaternityCenterSpecialNote.rejected, (state, action) => {
        state.maternityCenterSpecialNote.fetching = false
        state.maternityCenterSpecialNote.error = action.payload
        state.maternityCenterSpecialNote.data = null
      })
      // ========== 根据订单类型获取订单列表 ==========
      .addCase(fetchOrdersByType.pending, (state, action) => {
        const listKey =
          action.meta?.arg?.orderListScope === ORDER_LIST_REDUX_SCOPE.DISPATCH_PAGE
            ? ORDER_LIST_REDUX_SCOPE.DISPATCH_PAGE
            : ORDER_LIST_REDUX_SCOPE.ORDER_LIST_PAGE
        if (!action.meta?.arg?.silent) {
          state[listKey].fetching = true
          state[listKey].error = null
        }
      })
      .addCase(fetchOrdersByType.fulfilled, (state, action) => {
        const { query, queryForStore, data } = action.payload
        const listKey =
          action.meta?.arg?.orderListScope === ORDER_LIST_REDUX_SCOPE.DISPATCH_PAGE
            ? ORDER_LIST_REDUX_SCOPE.DISPATCH_PAGE
            : ORDER_LIST_REDUX_SCOPE.ORDER_LIST_PAGE
        if (!action.meta?.arg?.silent) {
          state[listKey].fetching = false
        }
        state[listKey].error = null
        state[listKey].query = queryForStore ?? query ?? {}
        state[listKey].data = data || { records: [], totalRow: 0, pageNumber: 1, pageSize: 20 }
      })
      .addCase(fetchOrdersByType.rejected, (state, action) => {
        const { query, queryForStore } = action.meta?.arg ?? {}
        const silent = action.meta?.arg?.silent
        const listKey =
          action.meta?.arg?.orderListScope === ORDER_LIST_REDUX_SCOPE.DISPATCH_PAGE
            ? ORDER_LIST_REDUX_SCOPE.DISPATCH_PAGE
            : ORDER_LIST_REDUX_SCOPE.ORDER_LIST_PAGE
        if (!silent) {
          state[listKey].fetching = false
          state[listKey].error = action.payload
          state[listKey].query = queryForStore ?? query ?? state[listKey].query ?? {}
          state[listKey].data = {
            records: [],
            totalRow: 0,
            pageNumber: 1,
            pageSize: 20
          }
        }
      })
  }
})

export const {
  resetOrderListState,
  resetDispatchOrderListState,
  resetOrderDetailState
} = maternityCenterOrderSlice.actions

export default maternityCenterOrderSlice.reducer
